/**
 * ProviderPicker — pure sort/filter/parsePrice helpers.
 *
 * All functions are pure (no side effects) so they can be unit-tested
 * in isolation without mounting any React component.
 */

import type { PickerOption, SortKey } from './types';

// Speed rank — lower number = faster
const SPEED_RANK: Record<NonNullable<PickerOption['speed']>, number> = {
  fastest: 1,
  fast: 2,
  medium: 3,
  slow: 4,
};

/**
 * Pull the smallest integer out of a pricing string for sort purposes.
 * "Free" / "Free / ~10" → 0, "~3" → 3, "~21" → 21, "249" → 249.
 * Returns Infinity if no number is found so unknown pricing sinks to the end.
 */
export function parsePriceTokens(pricing: string | undefined): number {
  if (!pricing) return Number.POSITIVE_INFINITY;
  if (/free/i.test(pricing)) return 0;
  const match = pricing.match(/\d+/);
  return match ? parseInt(match[0], 10) : Number.POSITIVE_INFINITY;
}

/**
 * Sort a list of PickerOptions by the given SortKey.
 * Returns a new array — does not mutate the input.
 */
export function sortOptions<TMeta>(
  options: PickerOption<TMeta>[],
  key: SortKey
): PickerOption<TMeta>[] {
  const copy = [...options];
  switch (key) {
    case 'price':
      copy.sort((a, b) => {
        const pa = a.pricingNumeric !== undefined ? a.pricingNumeric : parsePriceTokens(a.pricing);
        const pb = b.pricingNumeric !== undefined ? b.pricingNumeric : parsePriceTokens(b.pricing);
        return pa - pb;
      });
      break;
    case 'speed':
      copy.sort(
        (a, b) => (SPEED_RANK[a.speed ?? 'slow'] ?? 5) - (SPEED_RANK[b.speed ?? 'slow'] ?? 5)
      );
      break;
    case 'quality':
      copy.sort((a, b) => (b.quality ?? 0) - (a.quality ?? 0));
      break;
    case 'name':
      copy.sort((a, b) => a.label.localeCompare(b.label));
      break;
  }
  return copy;
}

/**
 * Filter options by search query (label + value, case-insensitive).
 * Returns same array reference if query is blank.
 */
export function filterBySearch<TMeta>(
  options: PickerOption<TMeta>[],
  query: string
): PickerOption<TMeta>[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(
    (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
  );
}

/**
 * Group options by the result of groupBy(option).
 * Options where groupBy returns null are placed under the key '_ungrouped'.
 * Preserves order within each group.
 */
export function groupOptions<TMeta>(
  options: PickerOption<TMeta>[],
  groupBy: (opt: PickerOption<TMeta>) => string | null
): Record<string, PickerOption<TMeta>[]> {
  const result: Record<string, PickerOption<TMeta>[]> = {};
  for (const opt of options) {
    const key = groupBy(opt) ?? '_ungrouped';
    if (!result[key]) result[key] = [];
    result[key].push(opt);
  }
  return result;
}
